/**
 * BUILT-THING-IS-A-TAB-1 — a generated thing joins the merchant's own menu.
 *
 * THE GAP THIS CLOSES. A generated artifact rendered only at the fixed settings
 * route, so Ghost was making a page somewhere else rather than building software
 * into the merchant's CRM. `App.tsx` is 178 literal route entries and
 * `navAccountLayer` MAY ONLY SUBTRACT by construction, so there was no way in.
 *
 * WHY THIS IS ADDITIVE AND THAT RULE IS NOT WEAKENED. The subtract-only rule
 * exists so a merchant override can never add back a node their ROLE was denied.
 * A built thing is not an override; it is a different source, joined at the same
 * hop `withRecordTypeSection` already uses (RECORD-TYPES-1), BEFORE the account
 * layer, so hide, rename and reorder apply to it exactly as they do to ours.
 *
 * BYTE IDENTICAL WHEN THERE ARE NONE, which is the same promise the record type
 * layer makes: no things, no section, same array contents.
 */

import type { NavChild, NavSection } from '../navConfig'

/**
 * WHICH KINDS MAY BE A TAB, READ FROM THE LIVE CONSTRAINT RATHER THAN INVENTED.
 *
 * `merchant_artifacts_kind` is `CHECK (kind = 'html')`, so this list is the
 * database's list. AN UNKNOWN KIND IS REFUSED, NEVER GUESSED AT: a kind we
 * cannot render is not a tab we can honour, and putting one in the menu would
 * promise a screen that cannot exist.
 */
export const BUILT_KINDS: readonly string[] = ['html']

/**
 * Only a live thing earns a tab. `merchant_artifacts_status` permits draft,
 * active and paused: a draft is mid build and a paused one was switched off on
 * purpose, and neither is something a merchant should find in their menu.
 */
export const BUILT_TAB_STATUS = 'active'

/** The section id, stable forever because the account layer keys on it. */
export const BUILT_SECTION_ID = 'built'

/**
 * The database's own slug rule, `merchant_artifacts_slug_format`, re-checked
 * here rather than trusted. The constraint lives in a database far away and
 * this value becomes an ADDRESS; a slug that arrived some other way must not
 * be able to shape a route.
 */
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/

/** Only what the menu needs. */
export interface BuiltThing {
  slug: string
  name: string
  kind: string
  status: string
  /** Removal is soft, so a removed thing is a row that still exists. */
  deleted_at?: string | null
}

/**
 * DERIVED, NOT REBUILT. The id and the address each have exactly one
 * definition, so the menu and the router can never disagree about where a
 * thing lives.
 */
export function builtNodeId(slug: string): string {
  return `${BUILT_SECTION_ID}.${slug}`
}

export function builtPath(slug: string): string {
  return `/dashboard/${BUILT_SECTION_ID}/${slug}`
}

/**
 * DOES THIS THING EARN A TAB?
 *
 * Four separate reasons it might not, and each is a different fact: it was
 * removed, it is not live yet, it is a kind we cannot render, or its slug could
 * not be an address. None of them is guessed at.
 */
export function earnsTab(thing: BuiltThing): boolean {
  if (thing.deleted_at != null) return false
  if (thing.status !== BUILT_TAB_STATUS) return false
  if (!BUILT_KINDS.includes(thing.kind)) return false
  return SLUG.test(thing.slug)
}

/** The tabs this account has earned, in the order given. */
export function builtTabs(
  things: ReadonlyArray<BuiltThing>,
  icon: React.ComponentType<{ className?: string }>,
): NavChild[] {
  return things.filter(earnsTab).map((t) => ({
    id: builtNodeId(t.slug),
    label: t.name,
    description: 'Something Ghost built for this business.',
    to: builtPath(t.slug),
    icon,
    primaryActionLabel: `Open ${t.name}`,
  }))
}

/**
 * The built section, joined before the account layer.
 *
 * AN ACCOUNT WITH NOTHING BUILT ADDS NO SECTION and gets the same array
 * contents it had. That is not politeness: an empty category in the menu is a
 * promise of something that is not there.
 */
export function withBuiltSection(
  sections: ReadonlyArray<NavSection>,
  things: ReadonlyArray<BuiltThing>,
  icon: React.ComponentType<{ className?: string }>,
): NavSection[] {
  const children = builtTabs(things, icon)
  if (children.length === 0) return [...sections]

  const section: NavSection = {
    id: BUILT_SECTION_ID,
    keywords: ['built', 'made', 'custom'],
    label: 'Built',
    description: 'The things Ghost built for this business.',
    to: children[0].to,
    icon,
    children,
  }

  // Before Settings when it is there, so a built thing sits with the working
  // parts of the menu rather than after the housekeeping.
  const at = sections.findIndex((s) => s.id === 'settings')
  if (at < 0) return [...sections, section]
  return [...sections.slice(0, at), section, ...sections.slice(at)]
}
